<script setup>
import { ref, onMounted, onUnmounted } from "vue";

const isScrolled = ref(false);
const menuOpen = ref(false);

let scrollHandler = null;

const openMenu = () => {
  menuOpen.value = true;
};
const closeMenu = () => {
  menuOpen.value = false;
};
const toggleMenu = () => {
  menuOpen.value = !menuOpen.value;
};

onMounted(() => {
  const header = document.querySelector("#header");
  if (!header) return;

  scrollHandler = () => {
    isScrolled.value = window.scrollY > 80;
  };

  window.addEventListener("scroll", scrollHandler, { passive: true });
  window.addEventListener("load", scrollHandler);
});

onUnmounted(() => {
  window.removeEventListener("scroll", scrollHandler);
});
</script>

<template>
  <header
    id="header"
    class="header d-flex align-items-center fixed-top"
    :class="{ 'scrolled-header': isScrolled }"
  >
    <div
      class="container position-relative d-flex align-items-center justify-content-between"
    >
      <!-- Logo: pequeño a la izquierda en móvil / centrado saliendo abajo en desktop -->
      <RouterLink to="/" class="logo-wrapper">
        <div class="logo-circle">
          <img src="../assets/img/Evolve/EGPro_logo.jpeg" alt="Evolve Logo" />
        </div>
      </RouterLink>

      <!-- ── NAV DESKTOP ── -->
      <nav id="navmenu" class="navmenu d-none d-xl-flex">
        <ul>
          <li><RouterLink to="/" exact-active-class="active">Inicio</RouterLink></li>
          <li>
            <RouterLink to="/informacion" active-class="active">Información</RouterLink>
          </li>
          <li>
            <RouterLink to="/peleadores" active-class="active">Peleadores</RouterLink>
          </li>
          <li>
            <RouterLink to="/patrocinadores" active-class="active"
              >Patrocinadores</RouterLink
            >
          </li>
          <li class="logo-spacer"></li>
          <li>
            <RouterLink to="/boletos" active-class="active" class="boletos-btn"
              >¡Boletos!</RouterLink
            >
          </li>
          <li>
            <RouterLink to="/categorias" active-class="active">Categorías</RouterLink>
          </li>
          <li>
            <RouterLink to="/contacto" active-class="active">Contáctanos</RouterLink>
          </li>
        </ul>
      </nav>

      <!-- ── BOTÓN HAMBURGUESA (solo móvil) ── -->
      <button
        class="hamburger-btn d-xl-none"
        @click="toggleMenu"
        :aria-label="menuOpen ? 'Cerrar menú' : 'Abrir menú'"
      >
        <i :class="menuOpen ? 'bi bi-x' : 'bi bi-list'"></i>
      </button>
    </div>

    <!-- ── OVERLAY (click fuera cierra el menú) ── -->
    <Teleport to="body">
      <div v-if="menuOpen" class="egpro-overlay" @click="closeMenu"></div>

      <!-- ── PANEL LATERAL MÓVIL ── -->
      <nav class="egpro-panel d-xl-none" :class="{ 'egpro-panel--open': menuOpen }">
        <!-- X para cerrar -->
        <button class="egpro-close-btn" @click="closeMenu" aria-label="Cerrar menú">
          <i class="bi bi-x"></i>
        </button>

        <ul class="egpro-nav-list">
          <li>
            <RouterLink to="/" exact-active-class="active" @click="closeMenu"
              >Inicio</RouterLink
            >
          </li>
          <li>
            <RouterLink to="/informacion" active-class="active" @click="closeMenu"
              >Información</RouterLink
            >
          </li>
          <li>
            <RouterLink to="/peleadores" active-class="active" @click="closeMenu"
              >Peleadores</RouterLink
            >
          </li>
          <li>
            <RouterLink to="/patrocinadores" active-class="active" @click="closeMenu"
              >Patrocinadores</RouterLink
            >
          </li>
          <li>
            <RouterLink
              to="/boletos"
              active-class="active boletos-pill"
              @click="closeMenu"
              >¡Boletos!</RouterLink
            >
          </li>
          <li>
            <RouterLink to="/categorias" active-class="active" @click="closeMenu"
              >Categorías</RouterLink
            >
          </li>
          <li>
            <RouterLink to="/contacto" active-class="active" @click="closeMenu"
              >Contáctanos</RouterLink
            >
          </li>
        </ul>

        <!-- Logo al fondo del panel -->
        <div class="egpro-panel-footer">
          <img src="../assets/img/Evolve/EGPro_logo.jpeg" alt="Evolve Grappling Pro" />
        </div>
      </nav>
    </Teleport>
  </header>
</template>

<style scoped>
/* ══ HEADER ════════════════════════════════════════════════════════════════ */
.header {
  background: rgba(10, 2, 2, 0.78) !important;
  backdrop-filter: blur(16px) !important;
  -webkit-backdrop-filter: blur(16px) !important;
  border-bottom: 2px solid rgba(204, 0, 0, 0.65) !important;
  box-shadow: 0 2px 20px rgba(204, 0, 0, 0.12), 0 1px 0 rgba(255, 255, 255, 0.03) inset;
  transition: background 0.3s ease, box-shadow 0.3s ease !important;
  z-index: 997;
  /* Anular borde azul del template */
  border-bottom-color: rgba(204, 0, 0, 0.65) !important;
}

.scrolled-header {
  background: rgba(6, 1, 1, 0.92) !important;
  box-shadow: 0 4px 24px rgba(204, 0, 0, 0.18) !important;
}

/* ══ LOGO ══════════════════════════════════════════════════════════════════ */
.logo-wrapper {
  display: block;
  text-decoration: none;
}

.logo-circle {
  border-radius: 10%;
  background: rgba(0, 0, 0, 0.55);
  border: 2px solid rgba(204, 0, 0, 0.55);
  box-shadow: 0 0 10px rgba(204, 0, 0, 0.18);
  display: flex;
  align-items: center;
  justify-content: center;

  height: 52px;
  padding: 5px;
  transition: border-color 0.3s ease, box-shadow 0.3s ease;
}
.logo-circle img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}
.logo-circle:hover {
  border-color: rgba(204, 0, 0, 0.9);
  box-shadow: 0 0 18px rgba(204, 0, 0, 0.35);
}

/* ══ NAV DESKTOP ═══════════════════════════════════════════════════════════ */
.navmenu {
  width: 100%;
}
.navmenu ul {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  align-items: center;
  width: 100%;
  gap: 4px;
}
.logo-spacer {
  flex-grow: 1;
}

.navmenu ul li a {
  font-family: "Oswald", sans-serif;
  font-size: 0.87rem;
  font-weight: 600;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: rgba(215, 215, 225, 0.88);
  text-decoration: none;
  padding: 6px 2px;
  position: relative;
  transition: color 0.25s ease;
}
/* Anular el ::before azul del template global */
.navmenu ul li a::before {
  background-color: #cc0000 !important;
}
.navmenu ul li a:hover,
.navmenu ul li a.active {
  color: #fff;
}

/* Pill rojo para boletos */
.navmenu ul li a.boletos-btn,
.navmenu ul li a[href*="boletos"] {
  background: linear-gradient(135deg, #7a0000, #cc0000);
  color: #fff !important;
  padding: 5px 16px !important;
  border-radius: 20px;
  border: 1px solid rgba(204, 0, 0, 0.4);
  box-shadow: 0 0 10px rgba(204, 0, 0, 0.25);
}
.navmenu ul li a.boletos-btn::before,
.navmenu ul li a[href*="boletos"]::before {
  display: none !important;
}
.navmenu ul li a.boletos-btn:hover,
.navmenu ul li a[href*="boletos"]:hover {
  background: linear-gradient(135deg, #900000, #e60000);
  box-shadow: 0 0 16px rgba(204, 0, 0, 0.4);
}

/* Logo centrado desktop sale del header */
@media (min-width: 1200px) {
  .logo-wrapper {
    position: absolute;
    left: 50%;
    bottom: 0;
    transform: translate(-50%, 50%);
    z-index: 999;
  }
  .logo-circle {
    width: 140px;
    height: 80px;
    border-width: 3px;
  }
  .scrolled-header .logo-circle {
    width: 80px;
    height: 55px;
  }
}

/* ══ BOTÓN HAMBURGUESA ═════════════════════════════════════════════════════ */
.hamburger-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  background: rgba(10, 2, 2, 0.65);
  border: 1px solid rgba(204, 0, 0, 0.45);
  border-radius: 8px;
  color: rgba(220, 220, 230, 0.95);
  font-size: 24px;
  line-height: 1;
  cursor: pointer;
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  transition: background 0.2s ease, border-color 0.2s ease, color 0.2s ease,
    transform 0.2s ease;
  /* Asegurar que sea visible — z alto */
  position: relative;
  z-index: 9999;
}
.hamburger-btn:hover {
  background: rgba(204, 0, 0, 0.25);
  border-color: rgba(204, 0, 0, 0.75);
  color: #fff;
  transform: scale(1.06);
}
.hamburger-btn i {
  line-height: 1;
}
</style>

<!-- Estilos globales para overlay y panel (Teleport sale del scope) -->
<style>
/* ══ OVERLAY ═══════════════════════════════════════════════════════════════ */
.egpro-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.62);
  backdrop-filter: blur(2px);
  -webkit-backdrop-filter: blur(2px);
  z-index: 9997;
}

/* ══ PANEL LATERAL ═════════════════════════════════════════════════════════ */
.egpro-panel {
  position: fixed;
  top: 0;
  right: -100%;
  width: min(290px, 82vw);
  height: 100dvh;
  background: rgba(8, 2, 2, 0.9);
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);
  border-left: 1px solid rgba(204, 0, 0, 0.3);
  box-shadow: -6px 0 36px rgba(0, 0, 0, 0.65);
  display: flex;
  flex-direction: column;
  padding-top: 72px;
  transition: right 0.32s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 9998;
  overflow-y: auto;
}
.egpro-panel--open {
  right: 0;
}

/* Botón X dentro del panel */
.egpro-close-btn {
  position: absolute;
  top: 16px;
  right: 16px;
  width: 38px;
  height: 38px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(204, 0, 0, 0.15);
  border: 1px solid rgba(204, 0, 0, 0.4);
  border-radius: 6px;
  color: rgba(220, 220, 230, 0.9);
  font-size: 22px;
  cursor: pointer;
  transition: background 0.2s, color 0.2s;
}
.egpro-close-btn:hover {
  background: rgba(204, 0, 0, 0.35);
  color: #fff;
}
.egpro-close-btn i {
  line-height: 1;
}

/* Lista de links del panel */
.egpro-nav-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
}
.egpro-nav-list li a {
  display: block;
  padding: 13px 28px;
  font-family: "Oswald", sans-serif;
  font-size: 0.92rem;
  font-weight: 600;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: rgba(215, 215, 225, 0.88);
  text-decoration: none;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
  transition: background 0.2s ease, color 0.2s ease, padding-left 0.2s ease;
}
.egpro-nav-list li a:hover,
.egpro-nav-list li a.active {
  color: #fff;
  background: rgba(204, 0, 0, 0.16);
  padding-left: 36px;
  border-left: 3px solid #cc0000;
}

/* Pill de boletos dentro del panel */
.egpro-nav-list li a[href*="boletos"] {
  margin: 10px 24px;
  padding: 9px 20px !important;
  border-radius: 4px;
  border-bottom: none;
  border-left: none;
  text-align: center;
  background: rgba(160, 0, 0, 0.35);
  color: #ff8080;
  border: 1px solid rgba(204, 0, 0, 0.45);
  box-shadow: none;
}
.egpro-nav-list li a[href*="boletos"]:hover {
  background: rgba(204, 0, 0, 0.45);
  color: #fff;
  padding-left: 20px !important;
  border-left: none;
}
.egpro-nav-list li a[href*="boletos"].active {
  background: rgba(204, 0, 0, 0.45);
  color: #fff;
  padding-left: 20px !important;
  border-left: none;
}

/* Logo al fondo del panel */
.egpro-panel-footer {
  margin-top: auto;
  padding: 20px 28px;
  border-top: 1px solid rgba(204, 0, 0, 0.18);
  display: flex;
  justify-content: center;
}
.egpro-panel-footer img {
  height: 72px;
  object-fit: contain;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  /* background: rgba(0, 0, 0, 0.35); */
}
</style>
